/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

// This class fills in the localeElements by using the data from Intl.DateTimeFormat.
// This data is used for parsing.

type WidthOptions = 'short' | 'narrow' | 'long';
export type CalendarWidthNames = 'abbreviated' | 'narrow' | 'wide';

type WeekdayNames = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

type MonthType = {
  '1': string;
  '2': string;
  '3': string;
  '4': string;
  '5': string;
  '6': string;
  '7': string;
  '8': string;
  '9': string;
  '10': string;
  '11': string;
  '12': string;
};

type DayType = {
  sun: string;
  mon: string;
  tue: string;
  wed: string;
  thu: string;
  fri: string;
  sat: string;
};
type DayPeriodType = {
  am: string;
  pm: string;
};
type ErasWidthType = {
  '0': string;
  '1': string;
};
type MonthWidthType = {
  abbreviated: MonthType;
  narrow: MonthType;
  wide: MonthType;
};
type DayWidthType = {
  abbreviated: DayType;
  narrow: DayType;
  wide: DayType;
};
type MonthsNodeType = {
  format: MonthWidthType;
  'stand-alone': MonthWidthType;
};
type DaysNodeType = {
  format: DayWidthType;
  'stand-alone': DayWidthType;
};

type DayPeriodFormatWidthType = {
  wide: DayPeriodType;
};
type DayPeriodsNodeType = {
  format: DayPeriodFormatWidthType;
};
type ErasType = {
  eraNarrow: ErasWidthType;
  eraAbbr: ErasWidthType;
  eraName: ErasWidthType;
};
export type CalendarNodeType = {
  months: MonthsNodeType;
  days: DaysNodeType;
  dayPeriods: DayPeriodsNodeType;
  eras: ErasType;
  locale: string;
};

type EraDesc = Array<{ era: string; start: string }>;

export class CalendarUtils {
  // calendars[locale][calendarname]
  public static calendars: Record<string, Record<string, CalendarNodeType>> | undefined;

  private static readonly _monthNamesFormatMap = {
    short: 'abbreviated',
    narrow: 'narrow',
    long: 'wide'
  } as const;
  private static readonly _weekdaysFormatMap = {
    1: 'sun',
    2: 'mon',
    3: 'tue',
    4: 'wed',
    5: 'thu',
    6: 'fri',
    7: 'sat'
  } as const;

  // The following locales have month name like '1<literal>' (where literal is the lang's character for 'month')
  // if we pass in the options
  // month:'long' or month:'short' the corresponding pattern in CLDR is 'M<literal>'
  // which is month numeric followed by the <literal>. As a result formatToParts
  // will return {type: month, value:1} followed by {type: literal, value:<literal>}.
  // These locales have same names for stand-alone and format month names.
  // As a work around we will get the format month names for these
  // locales using the stand-alone option instead of formatToParts.
  // For example: in ja-JP locale we have:
  // "months": {
  //   "format": {
  //       "abbreviated": {
  //           "1": "1\u6708",
  //  }
  // but the corresponding pattern is "yMMMd": "y\u5e74M\u6708d\u65e5"
  // so formatToParts will return
  // {type: month, value:1} followed by {type: literal, value:'\u6708d'}
  // As a result we get '1' as month name in the calendar data instead of '1\u6708'

  private static exceptionLocales = [
    'ja',
    'ja-JP',
    'zh',
    'zh-Hans',
    'zh-Hans-CN',
    'zh-Hans-HK',
    'zh-Hans-MO',
    'zh-Hans-SG',
    'zh-Hant',
    'zh-Hant-HK',
    'zh-Hant-MO',
    'zh-Hant-TW'
  ];
  // Uses the Intl.DateTimeFormat formatToParts to get the day periods.
  // Returns this object
  // "dayPeriods": {
  //  "format": {
  //    "wide": {
  //      "am": "AM",
  //      "pm": "PM"
  //    }}}
  private static _getDayPeriods(locale: string, calendar: string) {
    const date = new Date(2019, 0, 1, 0, 0, 0);

    function getDayPeriodsFromFormatToParts(formatter: Intl.DateTimeFormat) {
      const formatParts = formatter.formatToParts(date);
      const eraPart = formatParts.find((value) => value.type === 'dayPeriod');
      // TODO Era is failing for chinese calendar. Need to fix it
      if (eraPart) {
        return eraPart.value;
      }
      return '';
    }
    const options: {
      hour: Intl.DateTimeFormatOptions['hour'];
      hour12: Intl.DateTimeFormatOptions['hour12'];
    } = { hour: 'numeric', hour12: true };
    const formatterLocale = CalendarUtils.getFormatterLocale(locale, calendar);
    const formatter = new Intl.DateTimeFormat(formatterLocale, options);
    const amValue = getDayPeriodsFromFormatToParts(formatter);
    date.setHours(20);
    const pmValue = getDayPeriodsFromFormatToParts(formatter);
    return { format: { wide: { am: amValue, pm: pmValue } } };
  }

  private static getFormatterLocale(locale: string, calendar: string) {
    return locale + '-u-ca-' + calendar;
  }

  // Example of an eras from localeElements is this, but this fn gets all the data, not just eraAbbr.
  // "eras": {
  //   "eraAbbr": {
  //     "0": "BC",
  //     "1": "AD"
  //   }
  // },
  private static _getEras(locale: string, calendar: string) {
    const eras: EraDesc = [{ era: '0', start: '2000-02-11T00:00:00' }];
    const cal: ErasType = {
      eraNarrow: { '0': '', '1': '' },
      eraAbbr: { '0': '', '1': '' },
      eraName: { '0': '', '1': '' }
    };

    function getEraFromFormatToParts(formatter: Intl.DateTimeFormat, date: Date) {
      const formatParts = formatter.formatToParts(date);
      const eraPart = formatParts.find((value) => value.type === 'era');
      if (eraPart) {
        return eraPart.value;
      }
      return '';
    }

    const eraLenArray: Array<NonNullable<Intl.DateTimeFormatOptions['era']>> = [
      'narrow',
      'short',
      'long'
    ];
    const formatterLocale = CalendarUtils.getFormatterLocale(locale, calendar);
    // get the erra name for narrow, short, long.
    // e.g., A, AD, Anno Domini
    // this is used to map the era option value from Intl.DateTimeFormat to
    // what we want to short in our CalendarNode
    const eraMap = {
      narrow: 'eraNarrow',
      short: 'eraAbbr',
      long: 'eraName'
    };

    for (let i = 0; i < eras.length; i++) {
      const date = new Date(eras[i].start);
      for (let j = 0; j < eraLenArray.length; j++) {
        const eraLenName = eraLenArray[j];
        const options: {
          year: Intl.DateTimeFormatOptions['year'];
          month: Intl.DateTimeFormatOptions['month'];
          day: Intl.DateTimeFormatOptions['day'];
          era: Intl.DateTimeFormatOptions['era'];
        } = { year: 'numeric', month: 'numeric', day: 'numeric', era: eraLenName };
        const formatter = new Intl.DateTimeFormat(formatterLocale, options);
        const eraForLength = getEraFromFormatToParts(formatter, date);
        const eraNameForNode = eraMap[eraLenName] as keyof ErasType;
        const eraObj = { '0': eraForLength, '1': eraForLength };
        cal[eraNameForNode] = eraObj;
      }
    }
    return cal;
  }

  private static _fillMonthAndDays(
    locale: string,
    calendar: string,
    widthVal: CalendarWidthNames,
    options: Intl.DateTimeFormatOptions,
    isStandAlone: boolean
  ) {
    // These date are used to fill in the months and weekdays.
    // Each date is a different day of week and month. Like, the first is sun in January, the second is mon in Feb, etc.
    // If you know that a specific date, such as January 5, 2020, falls on a Sunday in Los Angeles, you can safely assume that it's also a Sunday in China.
    // This assumption is based on the consistency of the seven-day week cycle used worldwide
    // and the fact that days of the week are determined by the Earth's rotation.
    const dates: Array<Array<number>> = [
      [2020, 0, 5],
      [2020, 1, 3],
      [2020, 2, 3],
      [2020, 3, 1],
      [2020, 4, 7],
      [2020, 5, 5],
      [2020, 6, 4],
      [2020, 7, 1],
      [2020, 8, 1],
      [2020, 9, 1],
      [2020, 10, 1],
      [2020, 11, 1]
    ];

    function getMonthFromFormatToParts(formatParts: Intl.DateTimeFormatPart[]) {
      const monthPart = formatParts.find((value) => value.type === 'month');
      if (monthPart) {
        return monthPart.value;
      }
      return null;
    }

    function getWeekdayFromFormatToParts(formatParts: Intl.DateTimeFormatPart[]) {
      const weekdayPart = formatParts.find((value) => value.type === 'weekday');
      if (weekdayPart) {
        return weekdayPart.value;
      }
      return null;
    }

    const formatterLocale = CalendarUtils.getFormatterLocale(locale, calendar);
    const formatter = new Intl.DateTimeFormat(formatterLocale, options);

    type MonthFormatType = {
      abbreviated?: Partial<MonthType>;
      narrow?: Partial<MonthType>;
      wide?: Partial<MonthType>;
    };
    type DayFormatType = {
      abbreviated?: Partial<DayType>;
      narrow?: Partial<DayType>;
      wide?: Partial<DayType>;
    };
    const calMonthFormat: MonthFormatType = {};
    const calDayFormat: DayFormatType = {};

    // It loops from 0 to 12, dates is for each month. [0] is the year, [1] is the month, [2] is the day
    // create a new date with year, month, day. each date[j] is a different month of the year, and it also has a different weekday.
    for (let j = 0; j < dates.length; j++) {
      const index = j + 1; // 1 to 12
      const dayIndexVal: WeekdayNames =
        CalendarUtils._weekdaysFormatMap[index as keyof typeof CalendarUtils._weekdaysFormatMap];
      const date = new Date(dates[j][0], dates[j][1], dates[j][2]);
      const formatParts = formatter.formatToParts(date);
      let month;
      let weekday;
      // for stand-alone (e.g., {month: 'short'}), there is only one entry, so we can just get the
      // value of format. No need for formatToParts to pull out just the month piece.
      // Note that for ja format gives you the month kanji character too and formatToParts month is only the number. The kanji character is in the literal.
      if (isStandAlone) {
        month = formatter.format(date);
        weekday = formatter.format(date);
      } else {
        month = getMonthFromFormatToParts(formatParts);
        weekday = getWeekdayFromFormatToParts(formatParts);
      }
      // store the month and weekday in the obj; only fill in weekday if (index <= 7)
      if (calMonthFormat[widthVal] === undefined) {
        calMonthFormat[widthVal] = {};
      }
      // @ts-ignore
      calMonthFormat[widthVal][index] = month;
      if (index <= 7) {
        if (calDayFormat[widthVal] === undefined) {
          calDayFormat[widthVal] = {};
        }
        // @ts-ignore
        calDayFormat[widthVal][dayIndexVal] = weekday;
      }
    }
    return {
      monthFormat: calMonthFormat, // This will be an object like {'abbreviated': Partial<MonthType}
      dayFormat: calDayFormat
    };
  }

  private static _getFormatMonthAndDays(locale: string, calendar: string) {
    const calMonths = [];
    const calDays = [];
    // Intl.DateTimeFormat's month can take: "numeric" (e.g., 3)"2-digit" (e.g., 03)"long" (e.g., March)"short" (e.g., Mar) "narrow" (e.g., M).
    const types: Array<WidthOptions> = ['short', 'narrow', 'long'];
    for (let i = 0; i < types.length; i++) {
      const options: Intl.DateTimeFormatOptions = {
        month: types[i],
        weekday: types[i],
        year: 'numeric',
        day: 'numeric'
      };
      // types get mapped to short: 'abbreviated', narrow: 'narrow', long: 'wide'
      const widthVal = CalendarUtils._monthNamesFormatMap[types[i]]; // get alternate names for 'narrow', 'short', 'long'
      const monthDayObjForWidth = CalendarUtils._fillMonthAndDays(
        locale,
        calendar,
        widthVal,
        options,
        false
      );

      calMonths[i] = monthDayObjForWidth.monthFormat;
      calDays[i] = monthDayObjForWidth.dayFormat;
    }

    const myMonthObj = Object.assign({}, ...calMonths);
    const myDayObj = Object.assign({}, ...calDays);

    const myMonthFormatObj: Partial<MonthsNodeType> = {};
    myMonthFormatObj.format = myMonthObj;
    const myDayFormatObj: Partial<DaysNodeType> = {};
    myDayFormatObj.format = myDayObj;

    myMonthFormatObj['stand-alone'] = myMonthFormatObj.format;
    myDayFormatObj['stand-alone'] = myDayFormatObj.format;

    return {
      monthsNode: myMonthFormatObj as MonthsNodeType,
      daysNode: myDayFormatObj as DaysNodeType
    };
  }

  private static _getStandAloneDays(locale: string, calendar: string) {
    const calDays = [];
    // Intl.DateTimeFormat's weekday can take: "long" (e.g., Monday)"short" (e.g., Mon) "narrow" (e.g., M).
    // stand-alone weekday is displayed when the options conatin only one attribute weekday: options = {weekday:'long'}
    const types: Array<WidthOptions> = ['short', 'narrow', 'long'];
    for (let i = 0; i < types.length; i++) {
      const options: Intl.DateTimeFormatOptions = {
        weekday: types[i]
      };
      // types get mapped to short: 'abbreviated', narrow: 'narrow', long: 'wide'
      const widthVal = CalendarUtils._monthNamesFormatMap[types[i]]; // get alternate names for 'narrow', 'short', 'long'
      const dayObjForWidth = CalendarUtils._fillMonthAndDays(
        locale,
        calendar,
        widthVal,
        options,
        true
      );
      calDays[i] = dayObjForWidth.dayFormat;
    }
    const myDayObj = Object.assign({}, ...calDays);
    const myDayStandAloneObj = { 'stand-alone': {} };
    myDayStandAloneObj['stand-alone'] = myDayObj;
    return {
      daysNode: myDayStandAloneObj as DaysNodeType
    };
  }

  private static _getStandAloneMonths(locale: string, calendar: string) {
    const calMonths = [];
    // Intl.DateTimeFormat's month can take: "numeric" (e.g., 3)"2-digit" (e.g., 03)"long" (e.g., March)"short" (e.g., Mar) "narrow" (e.g., M).
    // stand-alone month is displayed when the options conatin only one attribute month: options = {month:'long'}
    const types: Array<WidthOptions> = ['short', 'narrow', 'long'];
    for (let i = 0; i < types.length; i++) {
      const options = {
        month: types[i]
      };
      // types get mapped to short: 'abbreviated', narrow: 'narrow', long: 'wide'
      const widthVal = CalendarUtils._monthNamesFormatMap[types[i]]; // get alternate names for 'narrow', 'short', 'long'
      const monthObjForWidth = CalendarUtils._fillMonthAndDays(
        locale,
        calendar,
        widthVal,
        options,
        true
      );
      calMonths[i] = monthObjForWidth.monthFormat;
    }
    const myMonthObj = Object.assign({}, ...calMonths);
    const myMonthStandAloneObj = { 'stand-alone': {} };
    myMonthStandAloneObj['stand-alone'] = myMonthObj;
    return {
      monthsNode: myMonthStandAloneObj as MonthsNodeType
    };
  }
  /**
   * Fills in CalendarUtils.calendars[locale][calendar] public static object.
   * @param locale locale name
   * @param calendar calendar name
   */
  public static getCalendar(locale: string, calendar: string): CalendarNodeType {
    CalendarUtils.calendars = CalendarUtils.calendars ?? {};
    CalendarUtils.calendars[locale] = CalendarUtils.calendars[locale] ?? {};

    if (CalendarUtils.calendars[locale][calendar] === undefined) {
      const dayPeriodsObj = CalendarUtils._getDayPeriods(locale, calendar);
      const erasObj = CalendarUtils._getEras(locale, calendar);
      let monthFormat;
      let dayFormat;
      const standAloneMonths = CalendarUtils._getStandAloneMonths(locale, calendar);
      const standAloneDays = CalendarUtils._getStandAloneDays(locale, calendar);
      const useStandAlone = CalendarUtils.exceptionLocales.includes(locale);
      // TODO this might only be needed for gregorian calendar. Need to check when
      // we support other calendars
      if (useStandAlone) {
        monthFormat = standAloneMonths.monthsNode['stand-alone'];
        dayFormat = standAloneDays.daysNode['stand-alone'];
      } else {
        const monthsDaysNodeObj = CalendarUtils._getFormatMonthAndDays(locale, calendar);
        monthFormat = monthsDaysNodeObj.monthsNode.format;
        dayFormat = monthsDaysNodeObj.daysNode.format;
      }
      const monthsNodes: MonthsNodeType = {
        format: monthFormat,
        'stand-alone': standAloneMonths.monthsNode['stand-alone']
      };
      const daysNodes: DaysNodeType = {
        format: dayFormat,
        'stand-alone': standAloneDays.daysNode['stand-alone']
      };
      CalendarUtils.calendars[locale][calendar] = {
        dayPeriods: dayPeriodsObj,
        months: monthsNodes,
        days: daysNodes,
        eras: erasObj,
        locale: locale
      };
    }
    return CalendarUtils.calendars[locale][calendar];
  }
}
